import { forwardRef, type ComponentPropsWithoutRef } from "react"
import * as DropdownMenu from "@radix-ui/react-dropdown-menu"

export const MenuButtonRoot = DropdownMenu.Root
export const MenuButtonTrigger = DropdownMenu.Trigger
export const MenuButtonPortal = DropdownMenu.Portal
export const MenuButtonArrow = DropdownMenu.Arrow
export const MenuButtonCheckboxItem = DropdownMenu.CheckboxItem
export const MenuButtonRadioGroup = DropdownMenu.RadioGroup
export const MenuButtonRadioItem = DropdownMenu.RadioItem
export const MenuButtonItemIndicator = DropdownMenu.ItemIndicator
export const MenuButtonSub = DropdownMenu.Sub
export const MenuButtonSubTrigger = DropdownMenu.SubTrigger
export const MenuButtonSubContent = DropdownMenu.SubContent

export type MenuButtonLabelProps = ComponentPropsWithoutRef<
  typeof DropdownMenu.Label
>

export const MenuButtonLabel = forwardRef<HTMLDivElement, MenuButtonLabelProps>(
  ({ className, ...props }, ref) => {
    return (
      <DropdownMenu.Label
        ref={ref}
        className={`menu-button-label ${className ?? ""}`}
        {...props}
      />
    )
  },
)

MenuButtonLabel.displayName = "MenuButtonLabel"

export type MenuButtonContentProps = ComponentPropsWithoutRef<
  typeof DropdownMenu.Content
>

export const MenuButtonContent = forwardRef<
  HTMLDivElement,
  MenuButtonContentProps
>(({ children, className, sideOffset, collisionPadding, ...props }, ref) => {
  return (
    <DropdownMenu.Content
      // Without setting asChild here and supplying an empty div, this component
      // doesn't seem to work correctly when wrapping it on our own MenuButtonContent.
      // Without these the content is rendered as an empty div. This seems to solve
      // the issue, so don't remove this.
      asChild
      ref={ref}
      sideOffset={sideOffset ?? 8}
      // Force the element to have 8px of padding from the edge of the viewport by default
      collisionPadding={collisionPadding ?? 8}
      className={`menu-button-content ${className ?? ""}`}
      {...props}
    >
      <div>{children}</div>
    </DropdownMenu.Content>
  )
})

MenuButtonContent.displayName = "MenuButtonContent"

export type MenuButtonGroupProps = ComponentPropsWithoutRef<
  typeof DropdownMenu.Group
>

export const MenuButtonGroup = forwardRef<HTMLDivElement, MenuButtonGroupProps>(
  ({ className, ...props }, ref) => {
    return (
      <DropdownMenu.Group
        ref={ref}
        className={`menu-button-group ${className ?? ""}`}
        {...props}
      />
    )
  },
)

MenuButtonGroup.displayName = "MenuButtonGroup"

export type MenuButtonItemProps = ComponentPropsWithoutRef<
  typeof DropdownMenu.Item
> & {
  "data-variant"?: "default" | "destructive"
}

export const MenuButtonItem = forwardRef<HTMLDivElement, MenuButtonItemProps>(
  ({ className, ...props }, ref) => {
    return (
      <DropdownMenu.Item
        ref={ref}
        className={`menu-button-item ${className ?? ""}`}
        {...props}
      />
    )
  },
)

MenuButtonItem.displayName = "MenuButtonItem"

export type MenuButtonSeparatorProps = ComponentPropsWithoutRef<
  typeof DropdownMenu.Separator
>

export const MenuButtonSeparator = forwardRef<
  HTMLDivElement,
  MenuButtonSeparatorProps
>(({ className, ...props }, ref) => {
  return (
    <DropdownMenu.Separator
      ref={ref}
      className={`menu-button-separator ${className ?? ""}`}
      {...props}
    />
  )
})

MenuButtonSeparator.displayName = "MenuButtonSeparator"
